/**
 * The one live workout, and the transaction that ends it.
 *
 * Before this file existed the product had two live workouts. Build kept a
 * start request in its own state and painted a fixed layer over the Build root;
 * the shell's `presentFullViewport("/train/live")` — the path the route table,
 * F-NAV-08's Ringside action and §9.2's keyboard scheme all address — mounted
 * `LiveWorkoutRoute`, which read a `LiveWorkoutProvider` nothing in the
 * application supplied and therefore rendered an opaque empty layer over an
 * `inert` shell with no way back. Neither of the two archived anything: both
 * end paths cleared their own state and returned, so a workout the user
 * actually trained left no record at all.
 *
 * One host closes all of it, and it sits **above** `<AppShell/>` (see
 * `AppRoot`) because that is the only scope both entry points can see: the
 * shell mounts the full-viewport route beside the four destination roots, so a
 * provider placed inside the Build root is invisible to it.
 *
 * Three properties are deliberate:
 *
 * 1. **The engine owns the ending, not this file.** `endEffort` is §4.5's write
 *    order — terminal snapshot durable, *then* history, *then* the checkpoint
 *    cleared — and `discardActiveEffort` is the §2.5 guarantee that a discarded
 *    workout has no path into History, enforced by the types rather than by a
 *    rule this host has to remember. All this file does is assemble their
 *    inputs and route their results.
 * 2. **`endedAt` is frozen once.** It is read from the clock at the moment the
 *    user ends the workout and re-used verbatim by every retry, so a session
 *    archived on the third attempt does not claim the user trained until then
 *    (F-TR-38). The same is true of the execution-entry ids, which are derived
 *    from the draft rather than minted, so a retry overwrites the first
 *    attempt's rows instead of accumulating ghosts.
 * 3. **A failed archive is not silent.** `retryPending` is the fail-closed
 *    state, not an error: the finished session is still held on the device.
 *    P27's `SaveRecovery` screen is mounted in its place — the same screen the
 *    open-effort host uses — so the user leaves with the work either archived
 *    or explicitly still held, never with a workout that vanished.
 *
 * It lives in `app-state/` for the reason `buildHost.tsx` and `journalHost.tsx`
 * do: it names P26's live surface, P27's recovery screen and P25's training
 * stores in one module, and §1.4 forbids a feature folder from importing
 * another. Like those two it is deliberately absent from the `app-state`
 * barrel — it reaches into feature code, and re-exporting it would pull the
 * live workout into every chunk that imports the barrel. `AppRoot` imports it
 * by path.
 */

import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ActivitySnapshot,
  ComboScore,
  CueToggleKey,
  EffortCheckpointId,
  EffortInstant,
  EffortSessionDraft,
  EffortSessionId,
  FrozenMilestoneCues,
  Milliseconds,
  SessionPlan,
  StructuredEffortCheckpoint,
  WorkoutExecutionLedger,
  WorkoutPlan,
} from "@ff/domain";
import {
  CUE_TOGGLE_DEFAULTS,
  DEVICE_NOUN,
  EFFORT_ACTIVITY_CATALOG,
  EFFORT_CHECKPOINT_SCHEMA_VERSION,
  HEAVY_BAG_45_BLUEPRINT,
  comboSequenceKey,
  deriveSessionPlan,
  effortInstantFromMilliseconds,
  effortInstantToMilliseconds,
  isBoxingRoundSegment,
  laterEffortInstant,
  materializeWorkoutPlan,
} from "@ff/domain";
import type { ExecutionLedger } from "@ff/engine";
import { discardActiveEffort, endEffort, executionEntries, terminalOutcome } from "@ff/engine";

// One module each, never a feature barrel: `../features/train/live` re-exports
// the whole live surface, and importing it here would put the screen, the deck
// and the Ringside board in the root chunk — which every document downloads
// before the user has started anything.
import { SaveRecovery } from "../features/train/effort/SaveRecovery";
import { LiveWorkoutProvider } from "../features/train/live/LiveWorkoutContext";
import { LIVE_ROUTE_PATH } from "../features/train/live/presentation";
import type { LiveWorkoutRequest } from "../features/train/live/session";
import { useTrainingStores } from "../features/train/home/trainingData";
// Sibling by sibling rather than through `./index`: this module is one of the
// ones the barrel does not re-export, and importing the barrel from inside it
// would be a cycle.
import { useWorkoutIdentity } from "./identity";
import { useNavigation } from "./navigation";
import { useClock, useIds } from "./ports";
import type { PreferenceCodec } from "./preferences";
import { booleanPreference, usePreference } from "./preferences";
import { browserComboFavorites } from "./recordStores";

/* ------------------------------------------------------------------------- *
 * The archive inputs
 * ------------------------------------------------------------------------- */

/**
 * A structured workout is boxing.
 *
 * `OPEN_EFFORT_ACTIVITY_KEYS` says it from the other side — "the count-up clock
 * does not offer boxing: boxing runs as a structured plan" — so the catalog's
 * boxing activity is the one every plan-driven session is recorded under. The
 * catalog value is used rather than a fabricated snapshot because the id is
 * persisted product data: a session recorded under an invented activity would
 * be a history row that no filter, count or catalog migration can ever match.
 */
export const LIVE_WORKOUT_ACTIVITY: ActivitySnapshot = EFFORT_ACTIVITY_CATALOG.boxing;

/**
 * What the live surface knows at the moment the workout ends.
 *
 * It is passed up rather than read out of a store because none of it is
 * persisted while the session runs: `useLiveWorkoutSession` holds the engine
 * cursor and the ledger in React state, and the ledger is the only honest
 * account of what was performed. Everything else here is what the terminal
 * checkpoint needs to describe the moment the session stopped.
 */
export interface LiveSessionReport {
  /** The frozen plan the engine actually ran, never the authoring tree (F-TR-06). */
  readonly plan: SessionPlan;
  readonly ledger: ExecutionLedger;
  readonly segmentIndex: number;
  readonly remaining: Milliseconds;
  readonly combo?: ComboScore;
  readonly activeDrill?: string;
}

export interface LiveArchiveInput {
  readonly sessionId: EffortSessionId;
  readonly checkpointId: EffortCheckpointId;
  readonly startedAt: EffortInstant;
  /** Frozen at the instant the user ended the workout; re-used by every retry. */
  readonly endedAt: EffortInstant;
  readonly report: LiveSessionReport;
}

/**
 * The engine's ledger, narrowed to the four fields a checkpoint stores.
 *
 * `ExecutionLedger` extends `WorkoutExecutionLedger` with per-step visit
 * structure the checkpoint has nowhere to put, and the checkpoint payload
 * decoder passes unknown fields straight through — so writing the whole thing
 * would persist engine state as though it were record state and hand a later
 * resume a shape its own contract never promised.
 */
function storedLedger(ledger: ExecutionLedger): WorkoutExecutionLedger {
  return {
    performedDuration: ledger.performedDuration,
    performedBySegment: ledger.performedBySegment,
    completedSegmentIds: ledger.completedSegmentIds,
    skippedSegmentIds: ledger.skippedSegmentIds,
  };
}

/** Whole milliseconds between two canonical instants, floored at zero. */
function durationBetween(from: EffortInstant, to: EffortInstant): Milliseconds {
  const span = effortInstantToMilliseconds(to) - effortInstantToMilliseconds(from);
  return Math.max(0, Math.round(span));
}

/**
 * The draft `sealEffortSession` turns into History.
 *
 * Two numbers carry the whole record and they come from different places on
 * purpose. **Active** duration is the ledger's banked total — measured time the
 * user actually spent in intervals, which is what a paused or abandoned session
 * must not inflate. **Wall** duration is the distance between the two frozen
 * instants, which is the only thing that can be said about a session that was
 * paused for ten minutes. The entries are then reconciled against the active
 * total, because the record's contract is that they sum to it exactly (F-TR-38).
 *
 * `timingEvidence` is deliberately absent: it is "required for manual records,
 * absent for engine-written and legacy ones", and an engine session has nothing
 * a user entered to attest to.
 */
export function liveEffortDraft(input: LiveArchiveInput): EffortSessionDraft {
  const { report } = input;
  const active = Math.max(0, Math.trunc(report.ledger.performedDuration));
  // The instant the record describes the effort as ending, which is what
  // `sealEffortSession` will use: a wall clock corrected behind the session's
  // own start makes `endedAt` earlier than `startedAt`, and the seal raises it.
  // Both audit dates follow it rather than the raw reading, because a record
  // whose `updatedAt` precedes its `endedAt` fails validation outright (F-EF-23)
  // — a backwards clock must not make a finished session unarchivable.
  const recorded = laterEffortInstant(input.startedAt, input.endedAt);

  return {
    id: input.sessionId,
    revision: 1,
    activity: LIVE_WORKOUT_ACTIVITY,
    source: "inAppPlan",
    // The browser cannot identify the device it runs on, and a record that
    // named one would be a fabricated audit fact.
    provenance: { deviceAuthority: "unknown" },
    startedAt: input.startedAt,
    wallDurationMilliseconds: Math.max(active, durationBetween(input.startedAt, input.endedAt)),
    activeDurationMilliseconds: active,
    requestedOutcome: terminalOutcome(report.plan.segments, report.ledger),
    originPlan: { id: report.plan.planId, name: report.plan.name },
    // The immutable snapshot the engine ran, so a later edit to the saved plan
    // cannot retroactively change what this record says was executed.
    executedPlan: report.plan,
    executionEntries: executionEntries({
      sessionId: input.sessionId,
      segments: report.plan.segments,
      ledger: report.ledger,
      activeDuration: active,
    }),
    ledger: storedLedger(report.ledger),
    // Both audit dates are the end of the session rather than "now", so a retry
    // produces byte-identical output.
    createdAt: recorded,
    updatedAt: recorded,
  };
}

/**
 * The terminal snapshot `endEffort` makes durable before it touches History.
 *
 * `terminalOutcome` is what makes it terminal: its presence in the store is the
 * signal that an idempotent retry is owed, which is how a crash between the
 * history write and the checkpoint clear is repaired on the next launch rather
 * than silently losing the session.
 */
export function liveTerminalCheckpoint(input: LiveArchiveInput): StructuredEffortCheckpoint {
  const { report } = input;
  const rounds = report.plan.segments.filter(
    (segment) =>
      isBoxingRoundSegment(segment) && report.ledger.completedSegmentIds.includes(segment.id),
  );

  return {
    shape: "structured",
    schemaVersion: EFFORT_CHECKPOINT_SCHEMA_VERSION,
    id: input.checkpointId,
    sessionId: input.sessionId,
    startedAt: input.startedAt,
    lastUpdated: input.endedAt,
    plan: report.plan,
    segmentIndex: report.segmentIndex,
    remaining: report.remaining,
    // A terminal snapshot must never tick again, whatever it is restored into.
    isRunning: false,
    isFinished: true,
    elapsed: report.ledger.performedDuration,
    completedRounds: rounds.length,
    // Skipped *combination presentations*, which the session controller does
    // not count — redrawing a call is not distinguished there from being handed
    // the next one. Reporting 0 is the honest reading for a build that has
    // never counted them; see the packet report.
    skippedCombos: 0,
    ...(report.combo === undefined ? {} : { combo: report.combo }),
    ...(report.activeDrill === undefined ? {} : { activeDrill: report.activeDrill }),
    executionLedger: storedLedger(report.ledger),
    terminalOutcome: terminalOutcome(report.plan.segments, report.ledger),
  };
}

/* ------------------------------------------------------------------------- *
 * The frozen cue snapshot (F-TR-06)
 * ------------------------------------------------------------------------- */

/**
 * The milestone half of §3.1's cue table.
 *
 * `FrozenMilestoneCues` is exactly these four flags, and `CueToggleKey` and
 * `CUE_TOGGLE_DEFAULTS` are `@ff/domain`'s, so nothing here invents a key or a
 * default. `Extract` rather than a bare union so a mistyped key collapses to
 * `never` and fails the `Record` instead of becoming a preference nobody writes.
 */
type MilestoneCueKey = Extract<
  CueToggleKey,
  "cues.halfway" | "cues.finalTen" | "cues.drillChange" | "cues.comboCalls"
>;

/**
 * Module-level because `usePreference` memoises the decoded value on the
 * codec's identity — one rebuilt per render decodes on every render.
 */
const MILESTONE_CODECS: Readonly<Record<MilestoneCueKey, PreferenceCodec<boolean>>> = {
  "cues.halfway": booleanPreference(CUE_TOGGLE_DEFAULTS["cues.halfway"]),
  "cues.finalTen": booleanPreference(CUE_TOGGLE_DEFAULTS["cues.finalTen"]),
  "cues.drillChange": booleanPreference(CUE_TOGGLE_DEFAULTS["cues.drillChange"]),
  "cues.comboCalls": booleanPreference(CUE_TOGGLE_DEFAULTS["cues.comboCalls"]),
};

/**
 * F-TR-06: the milestone toggles as they are *now*, ready to be frozen the
 * moment a session begins.
 *
 * `freezeMilestoneCues` takes a whole `CueSettings` — nine stored flags plus a
 * voice style — and discards all but these four. Reading the four directly
 * avoids assembling five settings the frozen value cannot carry, and the object
 * literal is checked against `FrozenMilestoneCues`, so a fifth milestone added
 * to the domain fails here rather than being silently dropped.
 */
function useMilestoneCues(): FrozenMilestoneCues {
  const [halfway] = usePreference("cues.halfway", MILESTONE_CODECS["cues.halfway"]);
  const [finalTen] = usePreference("cues.finalTen", MILESTONE_CODECS["cues.finalTen"]);
  const [drillChange] = usePreference("cues.drillChange", MILESTONE_CODECS["cues.drillChange"]);
  const [comboCalls] = usePreference("cues.comboCalls", MILESTONE_CODECS["cues.comboCalls"]);

  return useMemo<FrozenMilestoneCues>(
    () => ({ halfway, finalTen, drillChange, comboCalls }),
    [halfway, finalTen, drillChange, comboCalls],
  );
}

/* ------------------------------------------------------------------------- *
 * The host
 * ------------------------------------------------------------------------- */

/**
 * What a surface can do with the one live session.
 *
 * Deliberately no "is a workout running" flag: the only rule that would branch
 * on one is F-TR-40's replace-active confirmation, which is not this packet's,
 * and a field nothing reads is a promise about a decision nobody has taken.
 */
export interface LiveSessionHost {
  /** Freezes the plan, mints the session, and presents `/train/live`. */
  readonly start: (plan: WorkoutPlan) => void;
  /** F-TR-38: archives the session, then leaves the presentation. */
  readonly end: (report: LiveSessionReport) => void;
  /** §2.5: clears the checkpoint and writes no History entry. `false` means the
   *  checkpoint could not be verifiably removed, which raises the third alert. */
  readonly discard: () => Promise<boolean>;
  /** P27's save-recovery screen, mounted in place of the live surface after a
   *  failed archive. `null` at every other moment. */
  readonly saveRecovery: ReactNode | null;
  /** How many favourites the library held when this session began (F-TR-24). */
  readonly savedComboCount: number;
  /** Membership for one call, which is what the deck's Save/Saved slot asks. */
  readonly isComboSaved: (combo: ComboScore) => boolean;
}

const LiveSessionContext = createContext<LiveSessionHost | null>(null);

/**
 * `null` when no host is above the surface — which is a real state for a test
 * that mounts one Build surface on its own, and the reason Build's Start
 * renders as §8.6's inert label there rather than as a button that does
 * nothing. In the application the scope is always mounted.
 */
export function useOptionalLiveSession(): LiveSessionHost | null {
  return useContext(LiveSessionContext);
}

/** The live route's own accessor: it is only ever mounted inside the scope. */
export function useLiveSession(): LiveSessionHost {
  const host = useContext(LiveSessionContext);
  if (host === null) {
    throw new Error(
      "useLiveSession() requires an enclosing <LiveScope>, placed by <AppRoot> above <AppShell/>.",
    );
  }
  return host;
}

interface ActiveSession {
  readonly request: LiveWorkoutRequest;
  readonly startedAt: EffortInstant;
  readonly checkpointId: EffortCheckpointId;
}

interface PendingArchive {
  readonly input: LiveArchiveInput;
  readonly draft: EffortSessionDraft;
  readonly checkpoint: StructuredEffortCheckpoint;
}

interface RecoveryState {
  readonly pending: PendingArchive;
  readonly isRetrying: boolean;
}

export interface LiveScopeProps {
  readonly children?: ReactNode;
}

/**
 * Wraps the shell, holds the session, and owns both endings.
 *
 * The scope generates no box and renders no layer of its own: the shell already
 * owns the full-viewport presentation, including making the destinations behind
 * it `inert`, and a second layer painted here is exactly the parallel
 * presentation this file replaced.
 */
export function LiveScope({ children }: LiveScopeProps): ReactNode {
  const clock = useClock();
  const ids = useIds();
  const mint = useWorkoutIdentity();
  const navigation = useNavigation();
  const milestones = useMilestoneCues();
  // The one authorized bundle: effort store, reflections and the single
  // ticker-coalesced checkpoint store, built once per document. A second
  // checkpoint store would be a second coalescing window racing to write the
  // same singleton row.
  const stores = useTrainingStores();
  const favorites = useMemo(() => browserComboFavorites(), []);

  const [session, setSession] = useState<ActiveSession | null>(null);
  const [recovery, setRecovery] = useState<RecoveryState | null>(null);
  const [savedCombos, setSavedCombos] = useState<readonly ComboScore[]>([]);

  // Read through a ref inside the async endings: `end` and `discard` are handed
  // to a surface that keeps them across renders, and a stale closure there would
  // archive the session before the one the user is actually ending.
  const sessionRef = useRef<ActiveSession | null>(null);
  sessionRef.current = session;

  const sessionId = session?.request.sessionId ?? null;
  useEffect(() => {
    if (sessionId === null) return;
    let cancelled = false;
    void favorites.read().then((result) => {
      if (cancelled) return;
      // Fail closed: an unreadable library contributes nothing rather than a
      // short list that would read as "these are all your favourites".
      setSavedCombos(result.status === "loaded" ? result.value : []);
    });
    return () => {
      cancelled = true;
    };
    // Re-read once per session rather than continuously: F-TR-24's source label
    // describes the library the session was started with, and the favourites
    // store publishes no change notification to subscribe to anyway.
  }, [favorites, sessionId]);

  const savedComboKeys = useMemo(
    () => new Set(savedCombos.map((combo) => comboSequenceKey(combo))),
    [savedCombos],
  );

  const isComboSaved = useCallback(
    // By sequence, not by id: the library dedupes on `sequenceKey`, so a call
    // the user saved and an identical one generated later are the same combo to
    // it, and asking by id would report the second as unsaved.
    (combo: ComboScore): boolean => savedComboKeys.has(comboSequenceKey(combo)),
    [savedComboKeys],
  );

  const { dismissFullViewport, presentFullViewport } = navigation;

  const leave = useCallback((): void => {
    setSession(null);
    setRecovery(null);
    dismissFullViewport();
  }, [dismissFullViewport]);

  const start = useCallback(
    (plan: WorkoutPlan): void => {
      setSession({
        request: {
          sessionId: ids.effortSessionId(),
          // F-TR-06: the engine runs the frozen snapshot, never the authoring
          // tree, so an edit made after this point cannot change a running round.
          plan: deriveSessionPlan(plan),
          cues: milestones,
          // F-TR-07: a fresh start auto-starts. Only a resumed checkpoint does
          // not, and nothing in this build resumes one.
          autoStart: true,
        },
        startedAt: effortInstantFromMilliseconds(clock.now()),
        // Deliberately distinct from the session id: a checkpoint outlives the
        // document that wrote it and may never become a record at all.
        checkpointId: ids.effortCheckpointId(),
      });
      setRecovery(null);
      presentFullViewport(LIVE_ROUTE_PATH);
    },
    [clock, ids, milestones, presentFullViewport],
  );

  const archive = useCallback(
    async (pending: PendingArchive): Promise<void> => {
      const result = await endEffort(
        {
          termination: {
            kind: "archived",
            sessionId: pending.input.sessionId,
            endedAt: pending.input.endedAt,
          },
          draft: pending.draft,
          terminalCheckpoint: pending.checkpoint,
        },
        { effortStore: stores.effort, checkpointStore: stores.checkpoints },
      );

      if (result.kind === "archived") {
        // `checkpointResidue` is reported rather than raised: History is
        // confirmed, and refusing to show the user their session because a
        // stale snapshot survived would be a lie in the other direction. The
        // residue is cleared by the next launch's pending-archive retry.
        leave();
        return;
      }
      // `retryPending` is the fail-closed state, not an error: the finished
      // session is still held on the device with its original frozen end time.
      setRecovery({ pending, isRetrying: false });
    },
    [leave, stores],
  );

  const end = useCallback(
    (report: LiveSessionReport): void => {
      const active = sessionRef.current;
      if (active === null) return;

      const input: LiveArchiveInput = {
        sessionId: active.request.sessionId,
        checkpointId: active.checkpointId,
        startedAt: active.startedAt,
        endedAt: effortInstantFromMilliseconds(clock.now()),
        report,
      };
      void archive({
        input,
        draft: liveEffortDraft(input),
        checkpoint: liveTerminalCheckpoint(input),
      });
    },
    [archive, clock],
  );

  const discard = useCallback(async (): Promise<boolean> => {
    const active = sessionRef.current;
    if (active === null) return true;

    const result = await discardActiveEffort(
      {
        kind: "discarded",
        sessionId: active.request.sessionId,
        discardedAt: effortInstantFromMilliseconds(clock.now()),
      },
      { activity: LIVE_WORKOUT_ACTIVITY, startedAt: active.startedAt },
      "structured",
      { checkpointStore: stores.checkpoints },
    );

    // `DiscardedEffort` carries no outcome and no durations, and no function
    // maps one to an `ArchivedTermination` — so "a discarded workout writes no
    // History entry" is a property of the types here, not a rule this host
    // remembers. Nothing else is written on this path.
    if (result.kind !== "discarded") return false;
    leave();
    return true;
  }, [clock, leave, stores]);

  const retry = useCallback(
    (pending: PendingArchive): void => {
      setRecovery({ pending, isRetrying: true });
      void archive(pending);
    },
    [archive],
  );

  /**
   * F-NAV-08: Ringside is an action, and the surface it opens is this one.
   *
   * The sidebar presents the live path without naming a plan, so the host
   * supplies the one F-NAV-08 fixes — a freshly materialised Heavy Bag 45 —
   * whenever the presentation arrives with nothing running. Without this the
   * route mounts with no request and paints an empty layer over an `inert`
   * shell, which is exactly the dead end this packet closed.
   */
  const isLive = session !== null;
  const presentingLive = navigation.fullViewportPath === LIVE_ROUTE_PATH;
  useEffect(() => {
    if (!presentingLive || isLive) return;
    start(materializeWorkoutPlan(HEAVY_BAG_45_BLUEPRINT, mint));
  }, [presentingLive, isLive, mint, start]);

  const saveRecovery = useMemo<ReactNode | null>(() => {
    if (recovery === null) return null;
    const { pending } = recovery;
    return (
      <SaveRecovery
        deviceNoun={DEVICE_NOUN}
        isRetrying={recovery.isRetrying}
        onRetry={() => {
          retry(pending);
        }}
        // Closes without discarding: the terminal snapshot stays on disk and the
        // next attempt archives the same frozen record.
        onKeepLocal={leave}
      />
    );
  }, [recovery, retry, leave]);

  const host = useMemo<LiveSessionHost>(
    () => ({
      start,
      end,
      discard,
      saveRecovery,
      savedComboCount: savedCombos.length,
      isComboSaved,
    }),
    [start, end, discard, saveRecovery, savedCombos, isComboSaved],
  );

  return (
    <LiveSessionContext.Provider value={host}>
      {/*
        P26's plan channel, supplied by the application for the first time. The
        route reads its request from here; the lifecycle above travels through
        the host context beside it, so a surface that only needs the plan does
        not have to take the whole host.
      */}
      <LiveWorkoutProvider request={session?.request ?? null}>{children}</LiveWorkoutProvider>
    </LiveSessionContext.Provider>
  );
}
